'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import { Image } from './image';

export interface LogoProps {
  alt?: string;
  background?: string;
  backgroundColor?: string;
  fallbackSrc?: string;
  grayscale?: boolean;
  height?: number;
  imgHeight?: number;
  imgWidth?: number;
  src: string;
  width?: number;
}

const PADDING = 4;

/**
 * LogoBadge component standardizes the look of service and company logos.
 */
export function LogoBadge({
  alt,
  background,
  backgroundColor,
  fallbackSrc,
  grayscale = false,
  height = 24,
  imgHeight = 24,
  imgWidth = 24,
  src,
  width = 24,
}: LogoProps) {
  const { palette } = useTheme();
  const [currentSrc, setCurrentSrc] = useState(() => src);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleError = () => {
    if (fallbackSrc) {
      setCurrentSrc(fallbackSrc);
    }
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        background,
        backgroundColor: backgroundColor ?? palette.secondary.light,
        borderRadius: `${PADDING}px`,
        display: 'flex',
        height,
        justifyContent: 'center',
        padding: `${PADDING}px`,
        position: 'relative',
        width,
      }}
    >
      {!isLoaded && (
        <CircularProgress
          size={`${width - PADDING}px`}
          sx={{ position: 'absolute', top: PADDING / 2 }}
        />
      )}
      <Image
        alt={alt ?? src}
        height={imgHeight}
        onError={currentSrc === src ? handleError : undefined}
        onLoad={() => setIsLoaded(true)}
        src={currentSrc}
        style={{ borderRadius: `${PADDING}px`, filter: grayscale ? 'grayscale(100%)' : 'none' }}
        width={imgWidth}
      />
    </Box>
  );
}
